<svelte:options customElement={{ tag: "bt-editable-richtext", shadow: "none" }} />

<script lang="ts">
  import { onDestroy, createEventDispatcher, tick } from "svelte";
  import Icon from "$shared/icons/Icon.svelte";

  export let action = '';
  export let contents = '';
  export let styleClass: 'header' | '' = '';
  export let editable: boolean = false;
  export let formStyle = false;
  export let model = '';
  export let placeholder = '';
  export let property = '';

  const dispatch = createEventDispatcher();
  const editorId = `editable-rich-text-${Math.random().toString(36).slice(2)}`;
  let editor: any;
  let saveTimer: number | null = null;
  let isOpening = false;
  let isClosing = false;

  function debouncedSave() {
    if (saveTimer) window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(saveContent, 400);
  }

  function getName() {
    return `${model}[${property}]`;
  }

  async function toggleEditor() {
    if (!editable || isOpening || isClosing) return;

    if (!formStyle) {
      isOpening = true;
      formStyle = true;
      await tick();
      await initTiny();
      isOpening = false;
    } else {
      isClosing = true;
      destroyEditor();
      formStyle = false;
      isClosing = false;
    }
  }

  function whenTinyReady(cb: (tinymce: any) => void) {
    // @ts-ignore
    if (window.tinymce) return cb((window as any).tinymce);

    // Poll briefly until the script finishes loading
    const poll = setInterval(() => {
      // @ts-ignore
      if (window.tinymce) {
        clearInterval(poll);
        // @ts-ignore
        cb((window as any).tinymce);
      }
    }, 50);
    setTimeout(() => clearInterval(poll), 5000);
  }

  function initTiny(): Promise<void> {
    return new Promise((resolve) => {
      whenTinyReady((tinymce) => {
        if (editor) return resolve();

        tinymce.init({
          selector: `#${editorId}`,
          plugins: 'lists advlist fullscreen wordcount',
          toolbar:
            'undo redo | bold italic underline strikethrough | ' +
            'bullist numlist | alignleft aligncenter alignright | ' +
            'removeformat | fullscreen',
          toolbar_mode: 'wrap',
          menubar: 'file edit view tools',
          menu: {
            file:   { title: 'File',   items: 'print' }
          },
          statusbar: true,
          branding: true,
          promotion: false,
          elementpath: true,
          height: 260,
          formats: {
            underline:     { inline: 'u' },
            strikethrough: { inline: 's' },
            alignleft:   { selector: 'p,li', classes: 'tte-align-left' },
            aligncenter: { selector: 'p,li', classes: 'tte-align-center' },
            alignright:  { selector: 'p,li', classes: 'tte-align-right' }
          },
          valid_elements: 'p[class],br,strong/b,em/i,u,s,sub,sup,ul[class],ol[class],li[class]',
          invalid_elements: 'script,style,iframe,a,img,table,thead,tbody,tfoot,tr,th,td,caption,colgroup,col',
          paste_data_images: false,
          paste_block_drop: true,
          paste_webkit_styles: 'none',
          content_style: `
              .tte-align-left   { text-align: left; }
              .tte-align-center { text-align: center; }
            .tte-align-right  { text-align: right; }
          `,

          setup: (inst: any) => {
            editor = inst;
            inst.on('init', () => {
              inst.setContent(contents || '');
              inst.focus();
            });

            inst.on('change input undo redo keyup paste', () => {
              contents = inst.getContent();
              dispatch('change', { contents });
              debouncedSave();
            });
            inst.on('blur', () => {
              window.setTimeout(() => {
                saveContent();
                toggleEditor();
              }, 0);
            });
          },
          init_instance_callback: () => resolve()
        });
      });
    });
  }


  function isBlankHtml(html: string | null | undefined) {
    if (!html) return true;
    // strip tags & common “empty” things (p, br, nbsp)
    const txt = html
      .replace(/<br\s*\/?>/gi, '')
      .replace(/<\/?p[^>]*>/gi, '')
      .replace(/&nbsp;|&#160;/gi, ' ')
      .replace(/<[^>]+>/g, '')
      .trim();
    return txt.length === 0;
  }

  function destroyEditor() {
    if (editor) { editor.remove(); editor = null; }
  }

  function saveContent() {
    const payload: Record<string, any> = {};
    payload[model] = {};
    payload[model][property] = contents;

    fetch(action, {
      method: "PUT",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || "",
      },
      body: JSON.stringify(payload),
    });
  }

  onDestroy(() => { destroyEditor(); });
</script>

<div class="relative inline-block group w-full">
  <!-- DISPLAY MODE -->
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="{styleClass} {editable ? 'group-hover:pl-6 transition-all' : ''} prose prose-neutral max-w-none min-h-[1.25rem]"
    class:hidden={formStyle}
    class:cursor-pointer={editable}
    on:click={toggleEditor}
    aria-label="Click to edit rich text"
  >
    {#if isBlankHtml(contents)}
      <span class="text-neutral-400">{placeholder}</span>
    {:else}
      <div class="curation-prose-override">
        {@html contents}
      </div>
    {/if}
    {#if editable}
  <!-- Pencil overlay (show only when not editing) -->
      <div
        class:hidden={formStyle}
        class="absolute top-0 left-0 h-full flex items-center justify-center opacity-0 transition-all pointer-events-none {editable ? 'group-hover:opacity-100' : ''}"
      >
        <Icon icon="Edit" size="small" />
      </div>
    {/if}
  </div>

  <!-- EDIT MODE (Tiny binds to this textarea) -->
  {#if editable}
    <textarea
      id={editorId}
      name={getName()}
      class:hidden={!formStyle}
      style="width:100%"
    >{contents}</textarea>
  {/if}
</div>

<style>
  .header {
    font-weight: 600;
    font-family: theme('fontFamily.sans');
    line-height: 1.25;
    font-size: theme('fontSize.2xl');
    transition: all 150ms cubic-bezier(0.4, 0, 0.2, 1);
    cursor: pointer;
  }
</style>
